import {
  Component,
  ChangeDetectionStrategy,
  DestroyRef,
  Input,
  OnInit,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { BehaviorSubject, combineLatest, from } from 'rxjs';
import { filter, map, switchMap, tap } from 'rxjs/operators';
import { TrackingMapsRenderService } from '../tracking-maps-render.service';
import { LazyRenderService } from '../../../store/services/lazy-render.service';
import { AuthFacade } from '../../../store';
import { MapDataType } from './models';
import type { ShipmentOverviewMapComponent } from './shipment-overview-map/shipment-overview-map.component';

interface LazyMapRenderPayload {
  ShipmentOverviewMapComponent: typeof ShipmentOverviewMapComponent;
  mapData: MapDataType;
}

@Component({
  selector: 'lib-shipment-overview-map-container',
  imports: [CommonModule, TranslateModule],
  templateUrl: './shipment-overview-map-container.component.html',
  styleUrls: ['./shipment-overview-map-container.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ShipmentOverviewMapContainerComponent implements OnInit {
  @Input() set mapData(mapData: MapDataType | null) {
    this.mapData$.next(mapData);
  }

  @ViewChild('container', { read: ViewContainerRef, static: true })
  mapContainerRef: ViewContainerRef;

  private mapData$ = new BehaviorSubject<MapDataType | null>(null);

  constructor(
    private trackingMapRenderService: TrackingMapsRenderService,
    private lazyRenderService: LazyRenderService,
    public authFacade: AuthFacade,
    private destroyRef: DestroyRef,
  ) {}

  ngOnInit(): void {
    combineLatest([
      this.authFacade.mapsPluginAccepted$,
      this.trackingMapRenderService.isGoogleMapApiScriptAppended$,
      this.mapData$,
    ])
      .pipe(
        tap(([accepted, scriptAppended, mapData]: [boolean, boolean, MapDataType | null]) => {
          if (!accepted || !scriptAppended || !mapData) {
            this.mapContainerRef.clear();
          }
        }),
        filter(([accepted, scriptAppended, mapData]: [boolean, boolean, MapDataType | null]) =>
          Boolean(accepted && scriptAppended && mapData),
        ),
        switchMap(([, , mapData]: [boolean, boolean, MapDataType | null]) =>
          from(import('./shipment-overview-map/shipment-overview-map.component')).pipe(
            map(
              ({
                ShipmentOverviewMapComponent: Component,
              }: {
                ShipmentOverviewMapComponent: typeof ShipmentOverviewMapComponent;
              }): LazyMapRenderPayload => ({
                ShipmentOverviewMapComponent: Component,
                mapData: mapData as MapDataType,
              }),
            ),
          ),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(({ ShipmentOverviewMapComponent: Component, mapData }: LazyMapRenderPayload) => {
        const componentRef = this.lazyRenderService.render(this.mapContainerRef, Component);
        componentRef.instance.mapData = mapData;
      });
  }
}
